import React, { useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";

// Helpers
import getCountdown from "helpers/getCountdown";

// Components
import FloatingModal from "components/modal/FloatingModal";

// Images & Icons
import logoESGAwardsTransparent from "assets/images/ESG_Awards_transparent_logo.png";
import central_blue_cirlce from "assets/images/logo/central_blue_cirlce.png";
import green_core_element from "assets/images/logo/green_core_element.png";
import radiating_petals from "assets/images/logo/radiating_petals.png";

const About = () => {
  const { t } = useTranslation();

  const [isOpenModalLogoDesc, setIsOpenModalLogoDesc] = useState(false);

  const logoPieces = [
    {
      title: "Radiating Petals",
      icon: radiating_petals,
      description:
        "Represent inspiration, integrity, and impact. The outward motion signifies how strong governance radiates positive influence—illuminating others and driving change across the ESG landscape.",
    },
    {
      title: "Central Blue Circle",
      icon: central_blue_cirlce,
      description:
        "Symbolizes trust, accountability, and unity. It reflects the stability and structure that sound governance provides — a calm center amid dynamic progress.",
    },
    {
      title: "Green Core Element",
      icon: green_core_element,
      description:
        "Represents growth, sustainability, and continuous improvement. It’s the heart of the Awards’ vision: governance that not only maintains order but nurtures progress and purpose.",
    },
  ];

  const timeline = [
    {
      title: "Download RFI (Request for Information)",
      date: "16 February - 22 May 2026",
      start_date: moment("2026-02-16 00:00"),
      end_date: moment("2026-05-22 17:00"),
    },
    {
      title: "Submit RFI & Payment",
      date: "16 February - 22 May 2026 @ 17.00 GMT+7",
      start_date: moment("2026-02-16 00:00"),
      end_date: moment("2026-05-22 17:00"),
    },
    {
      title: "First Screening - Administrative",
      date: "25 May - 4 June 2026",
      start_date: moment("2026-05-25 00:00"),
      end_date: moment("2026-06-04 23:59"),
    },
    {
      title: "Announcement for Candidates for Judging Process",
      date: "5 June 2026",
      start_date: moment("2026-06-05 00:00"),
      end_date: moment("2026-06-05 23:59"),
    },
    {
      title: "Second Screening - Judging Panel Online Interview",
      date: "8 June - 12 June 2026",
      start_date: moment("2026-06-08 00:00"),
      end_date: moment("2026-06-12 23:59"),
    },
    {
      title: "Announcement for Nominations of the Awards",
      date: "15 June 2026",
      start_date: moment("2026-06-15 00:00"),
      end_date: moment("2026-06-15 23:59"),
    },
    {
      title:
        "Announcement for Winners of the Awards / Workshop & Awarding Night",
      date: "18 June 2026",
      start_date: moment("2026-06-18 00:00"),
      end_date: moment("2026-06-18 23:59"),
    },
  ];
  const currentDate = moment();
  const targetDate = moment("2026-05-22 17:00", "YYYY-MM-DD HH:mm");
  const timeLeft = getCountdown(targetDate);
  const labels = ["Days", "Hours", "Minutes", "Seconds"];

  const EachLogoPiece = ({ icon, title, description }) => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-[56px_1fr] gap-4 sm:gap-6 items-start">
        {/* Icon */}
        <div className="flex justify-center sm:justify-start">
          <img src={icon} alt={title} className="object-contain w-12 h-12" />
        </div>

        {/* Text */}
        <div className="text-center sm:text-left">
          <h3 className="text-lg font-semibold text-[#1E1E1E] mb-2">{title}</h3>
          <p className="text-sm md:text-base leading-relaxed text-[#333333] text-justify">
            {description}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div id="aboutContainer">
      <section>
        <div className="space-y-8 app-main-container">
          <p className="text-xl font-bold text-center sm:text-2xl app-animation-translate-start section-title">
            {t("aboutTheAwards")}
          </p>
          <div className="flex flex-col items-start justify-between w-full gap-10 mx-auto lg:flex-row max-w-7xl">
            {/* LEFT SIDE */}
            <div className="flex-1 space-y-8">
              {/* Deadline Card */}
              <div className="flex flex-col lg:flex-row items-center justify-center gap-2 p-2 text-center border border-[#8DC63F] shadow-sm lg:gap-10 rounded-2xl">
                <div className="flex items-center min-w-[120px] flex-col">
                  <img
                    src={logoESGAwardsTransparent}
                    alt="ESG Awards logo"
                    className="w-20 w-32 h-auto"
                  />
                  <button
                    className="px-2 py-1 text-sm sm:px-4 text-sm font-bold text-white rounded-md bg-[#8DC63F] capitalize hover:bg-lime-600"
                    onClick={() => setIsOpenModalLogoDesc(true)}
                  >
                    about logo
                  </button>
                </div>
                <div className="">
                  <h2 className="mb-2 text-xl font-semibold text-gray-800 sm:text-3xl">
                    Deadline Submission
                  </h2>
                  <div className="flex flex-wrap items-center justify-center gap-1 sm:flex-nowrap sm:gap-2">
                    {labels.map((label, i) => (
                      <div
                        key={label}
                        className="flex flex-row items-center justify-center"
                      >
                        <div
                          key={label}
                          className="px-2 py-1 text-center bg-gray-100 rounded-md sm:py-3 sm:px-4"
                        >
                          <p className="text-xl font-bold text-[#005A99] sm:text-3xl">
                            {timeLeft[label]}
                          </p>
                          <p className="text-[10px] text-gray-500 sm:text-xs">
                            {label}
                          </p>
                        </div>
                        {i !== labels.length - 1 ? (
                          <span className="ml-2 text-xl font-bold text-blue-800 sm:text-3xl">
                            :
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* About Section */}
              <div>
                <p className="text-xl font-bold text-left text-justify sm:text-2xl app-animation-translate-start">
                  {t("aboutTheAwardsHeader")}
                </p>
                <p className="mt-2 text-xs text-justify sm:text-sm md:text-md lg:text-lg app-animation-translate-start">
                  {t("aboutTheAwardsText1")}
                </p>
                <p className="mt-4 text-xs text-justify sm:text-sm md:text-md lg:text-lg app-animation-translate-start">
                  {t("aboutTheAwardsText2")}
                </p>
                <p className="mt-4 text-xs text-justify sm:text-sm md:text-md lg:text-lg app-animation-translate-start">
                  {t("aboutTheAwardsText3")}
                </p>
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="bg-[#005A99] text-white rounded-2xl p-6 shadow-md mx-auto sm:mx-0">
              <h2 className="mb-6 text-xl font-bold text-center sm:text-2xl sm:text-left">
                Awards Important Dates
              </h2>
              <div className="relative pl-6">
                <div className="absolute left-3 top-2 bottom-2 w-[2px] bg-gray-200/50"></div>

                {timeline.map((item, i) => {
                  const isPassed = item.end_date.isSameOrBefore(currentDate);
                  const isRunning = currentDate.isBetween(
                    item.start_date,
                    item.end_date,
                    null,
                    "[]",
                  );

                  return (
                    <div key={i} className="relative mb-6 last:mb-0">
                      <div
                        className={`absolute -left-[17px] top-1.5 w-3 h-3 rounded-full ${
                          isRunning || isPassed
                            ? "bg-[#8DC63F]"
                            : "bg-orange-500"
                        }`}
                      ></div>
                      <div className="text-center sm:text-left">
                        <h3 className="font-semibold text-white">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-200">{item.date}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FloatingModal
        isOpen={isOpenModalLogoDesc}
        onClose={() => setIsOpenModalLogoDesc(false)}
      >
        <div className="flex flex-col items-center gap-8 px-4 py-6 overflow-y-auto">
          <div className="flex flex-wrap items-center justify-center w-full gap-3 px-2">
            <img
              src={logoESGAwardsTransparent}
              alt="ESG Awards logo"
              className="object-contain w-40 h-auto"
            />
            <div className="bg-[#005A99] rounded-xl">
              <div className="px-4 py-2 text-xl md:text-2xl font-bold text-center text-[#ffffff] leading-snug italic">
                Governance that Guides and Inspires Actions
              </div>
            </div>
          </div>

          <div className="max-w-[95%]">
            {logoPieces && Array.isArray(logoPieces) && logoPieces.length ? (
              <div className="flex flex-col gap-6 px-1 sm:gap-8 sm:px-0">
                {logoPieces.map((eachPiece, idx) => {
                  return (
                    <EachLogoPiece
                      key={idx}
                      icon={eachPiece.icon}
                      title={eachPiece.title}
                      description={eachPiece.description}
                    />
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </FloatingModal>
    </div>
  );
};

export default About;
